/**
 * useToast Hook
 *
 * Provides convenient methods for showing toast notifications.
 * Used by useApi hooks and components for user feedback.
 *
 * @example
 * ```ts
 * const toast = useToast();
 * toast.success('Saved!', 'Your changes have been saved.');
 * toast.error('Error', 'Failed to save changes.');
 * toast.addToast({ type: 'success', title: 'Done', message: 'Published!' });
 * ```
 */

import { useToastStore, type ToastType } from '../stores/toastStore';

export interface UseToastReturn {
  /** Show a success toast */
  success: (title: string, message?: string) => string;
  /** Show an error toast */
  error: (title: string, message?: string) => string;
  /** Show a warning toast */
  warning: (title: string, message?: string) => string;
  /** Show an info toast */
  info: (title: string, message?: string) => string;
  /** Show a custom toast */
  show: (type: ToastType, title: string, message?: string, duration?: number) => string;
  /** Add a toast via an object parameter */
  addToast: (params: { type: ToastType; title: string; message?: string; duration?: number }) => string;
  /** Remove a specific toast by ID */
  dismiss: (id: string) => void;
  /** Clear all toasts */
  clearAll: () => void;
}

export function useToast(): UseToastReturn {
  const addToast = useToastStore((state) => state.addToast);
  const removeToast = useToastStore((state) => state.removeToast);
  const clearToasts = useToastStore((state) => state.clearToasts);

  const show = (type: ToastType, title: string, message?: string, duration?: number): string => {
    return addToast({ type, title, message, duration });
  };

  return {
    success: (title: string, message?: string) => show('success', title, message),
    error: (title: string, message?: string) => show('error', title, message, 8000),
    warning: (title: string, message?: string) => show('warning', title, message, 6000),
    info: (title: string, message?: string) => show('info', title, message),
    show,
    addToast: (params: { type: ToastType; title: string; message?: string; duration?: number }) =>
      show(params.type, params.title, params.message, params.duration),
    dismiss: removeToast,
    clearAll: clearToasts,
  };
}

/**
 * Non-hook toast utility for use outside React components (e.g., interceptors).
 * Accesses the store directly.
 */
export const toast = {
  success: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'success', title, message }),
  error: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'error', title, message, duration: 8000 }),
  warning: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'warning', title, message, duration: 6000 }),
  info: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'info', title, message }),
  dismiss: (id: string) => useToastStore.getState().removeToast(id),
  clearAll: () => useToastStore.getState().clearToasts(),
};